import api from './api'

export interface PaymentDocument {
  uuid: string
  club_uuid: string
  user_uuid: string
  type: 'terms' | 'privacy' | 'other'
  title: string
  name?: string
  content: string
  version: number
  is_active: boolean
  created_at: string
  updated_at: string
  created_by?: {
    uuid: string
    email: string
  }
}

export interface CreatePaymentDocumentDto {
  type: 'terms' | 'privacy' | 'other'
  title: string
  name?: string
  content: string
}

export interface UpdatePaymentDocumentDto {
  title?: string
  name?: string
  content: string
}

export interface CheckRequirementsResponse {
  has_terms: boolean
  has_privacy: boolean
  can_add_payment_method: boolean
}

/**
 * Payment Document Service
 * Handles all payment document-related API calls
 */
class PaymentDocumentService {
  /**
   * Get list of payment documents for a club
   * GET v1/{club}/payment-documents
   */
  async getDocuments(clubUuid: string, type?: 'terms' | 'privacy' | 'other'): Promise<{ documents: PaymentDocument[] }> {
    const params = type ? { type } : {}
    const response = await api.get(`v1/${clubUuid}/payment-documents`, { params })
    return response.data
  }

  /**
   * Get single payment document
   * GET v1/{club}/payment-documents/{documentUuid}
   */
  async getDocument(clubUuid: string, documentUuid: string): Promise<{ document: PaymentDocument }> {
    const response = await api.get(`v1/${clubUuid}/payment-documents/${documentUuid}`)
    return response.data
  }

  /**
   * Create new payment document
   * POST v1/{club}/payment-documents
   */
  async createDocument(clubUuid: string, data: CreatePaymentDocumentDto): Promise<{ message: string; document: PaymentDocument }> {
    const response = await api.post(`v1/${clubUuid}/payment-documents`, data)
    return response.data
  }

  /**
   * Update payment document
   * PUT v1/{club}/payment-documents/{documentUuid}
   */
  async updateDocument(clubUuid: string, documentUuid: string, data: UpdatePaymentDocumentDto): Promise<{ message: string; document: PaymentDocument }> {
    const response = await api.put(`v1/${clubUuid}/payment-documents/${documentUuid}`, data)
    return response.data
  }

  /**
   * Delete payment document
   * DELETE v1/{club}/payment-documents/{documentUuid}
   */
  async deleteDocument(clubUuid: string, documentUuid: string): Promise<{ message: string }> {
    const response = await api.delete(`v1/${clubUuid}/payment-documents/${documentUuid}`)
    return response.data
  }

  /**
   * Get document history (for terms and privacy only)
   * GET v1/{club}/payment-documents/history/{type}
   */
  async getDocumentHistory(clubUuid: string, type: 'terms' | 'privacy'): Promise<{ versions: PaymentDocument[] }> {
    const response = await api.get(`v1/${clubUuid}/payment-documents/history/${type}`)
    return response.data
  }

  /**
   * Check if club has required documents
   * GET v1/{club}/payment-documents/check-requirements
   */
  async checkRequirements(clubUuid: string): Promise<CheckRequirementsResponse> {
    const response = await api.get(`v1/${clubUuid}/payment-documents/check-requirements`)
    return response.data
  }

  /**
   * Get draft document content with club placeholders substituted
   * GET v1/{club}/payment-documents/draft/{type}
   */
  async getDraft(clubUuid: string, type: 'terms' | 'privacy', lang: string): Promise<{ draft: boolean; content: string; lang: string } | null> {
    try {
      const response = await api.get(`v1/${clubUuid}/payment-documents/draft/${type}`, {
        headers: { 'Accept-Language': lang }
      })
      return response.data
    } catch {
      return null
    }
  }

  /**
   * Get active document version for a specific type
   */
  async getActiveDocumentVersion(clubUuid: string, type: 'terms' | 'privacy' | 'other'): Promise<PaymentDocument | null> {
    const { documents } = await this.getDocuments(clubUuid, type)
    return documents.find(doc => doc.is_active) || null
  }
}

export default new PaymentDocumentService()
